import { shallow } from 'zustand/shallow'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import SvgIcon from '@mui/material/SvgIcon'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { toast } from 'react-toastify'
import { useWeb3Modal } from '@web3modal/react'
import {
  useAccount,
  useDisconnect,
  useNetwork,
  // useSwitchNetwork
} from 'wagmi'

import { useChainAndNetwork } from 'src/store/chainAndNetwork'
import useBlockchainAdapter from '../../hooks/useBlockchainAdapter'
import { useAuthDispatch } from '../../contexts/authContext'
import { evmNetworkIds, evmNetworkIdsType } from '../../util/chainIds'

const ConnectEvmInfo = ({
  infoText,
  isAddressSet,
  setAddress,
}: {
  infoText: string
  isAddressSet: boolean
  setAddress: (address: string) => void
}) => {
  const adapter = useBlockchainAdapter()
  const { open } = useWeb3Modal()
  const { chain } = useNetwork()

  const { chain: selectedChain, network } = useChainAndNetwork(
    (state) => ({
      chain: state.chain,
      network: state.network,
    }),
    shallow,
  )
  const setShowNetworkModal = useAuthDispatch()

  const { isConnected: isWalletConnected } = useAccount({
    onConnect({ address }) {
      if (address) {
        setAddress(address)
      }

      if (
        chain?.id !== evmNetworkIds[selectedChain.toUpperCase() as keyof evmNetworkIdsType][network]
      ) {
        setShowNetworkModal({
          type: 'SWITCHNETWORKMODAL',
          payload: true,
        })
      }
    },
  })

  const { disconnect } = useDisconnect({
    onSuccess() {
      setAddress('')
    },
  })

  const handleConnectMetamask = async () => {
    try {
      const address = await adapter.metamask.connect()
      setAddress(address)

      const metamaskChainIdMismatch =
        adapter.metamask?.connector?.isConnected() &&
        adapter.metamask?.connector?.selectedAddress &&
        adapter.metamask?.connector?._state?.isUnlocked &&
        parseInt(adapter.metamask?.connector?.networkVersion) !==
          evmNetworkIds[selectedChain.toUpperCase() as keyof evmNetworkIdsType][network]

      if (metamaskChainIdMismatch) {
        adapter.metamask?.switchNetwork(selectedChain, network)
      }
    } catch (error) {
      console.error(error)
      toast.error(
        'Please open the metamask extension manually and unlock your account. Then refresh the page.',
        {
          progressClassName: 'simpleBar',
        },
      )
    }
  }

  const handleOpenWalletConnect = () => {
    open()
  }

  const renderMetamaskButton = () => {
    return isAddressSet ? null : (
      <Button
        variant="contained"
        color="warning"
        onClick={handleConnectMetamask}
        sx={{ fontWeight: '600', fontFamily: 'SpaceGrotesk, sans-serif !important' }}>
        Connect Metamask
      </Button>
    )
  }

  const renderConnectWalletConnect = () => {
    return isWalletConnected || isAddressSet ? null : (
      <Button
        className="btn btn-primary"
        variant="contained"
        color="info"
        onClick={handleOpenWalletConnect}>
        Wallet Connect
      </Button>
    )
  }

  const renderDisconnectWalletConnect = () => {
    return isWalletConnected || isAddressSet ? (
      <Button
        variant="outlined"
        color="error"
        onClick={() => disconnect()}
        sx={{ fontWeight: '600', fontFamily: 'Space Grotesk, sans-serif' }}>
        Disconnect Wallet
      </Button>
    ) : null
  }

  return isAddressSet ? (
    renderDisconnectWalletConnect()
  ) : (
    <Grid container spacing={2}>
      {infoText ? (
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
          <Paper
            elevation={0}
            sx={{
              background: 'rgba(211, 47, 47, 0.15)',
              color: '#fff',
              padding: '18px 10px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '15px',
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: '8px',
            }}>
            <SvgIcon component={ErrorOutlineOutlinedIcon} sx={{ color: 'rgba(211, 47, 47, 1)' }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: '500',
                color: 'rgba(211, 47, 47, 1)',
                fontFamily: 'Space Grotesk, sans-serif',
              }}>
              {infoText}
            </Typography>
          </Paper>
        </Grid>
      ) : null}
      <Grid
        item
        xs={12}
        md={9}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '15px',
            width: '100%',
          }}>
          {renderMetamaskButton()}
          {renderConnectWalletConnect()}
        </Box>
      </Grid>
    </Grid>
  )
}

export default ConnectEvmInfo
